import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Alert, Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {DefaultPaginationInfo} from '../../../../../../../core/constants/enums';
import {BizPointAdminReviewQueueDataGridColumnNames} from '../../../../../../../core/constants/table/data-grid-column-keys';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from '../../../../../../../packages/data-grid';
import useSearch from '../../../../../../hooks/use-search';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import Button from '../../../../../../components/base/button/button';
import SearchInput from '../../../../../../components/base/input/search_input';
import PageHeader from '../../components/page-header';
import {paginateMockItems} from '../../mock-search';
import {mockReviewQueue} from '../../mock-data';
import ResolveQueueItemDialog from './resolve-queue-item-dialog';

const dataGridSaveConfig = {name: 'BizPointAdminReviewQueueDataGrid', version: 1};
const col = BizPointAdminReviewQueueDataGridColumnNames;

const searchOptions = {
    keywordFields: ['wallet', 'rule', 'reason'],
    sortFieldMap: {
        [col.wallet]: 'wallet',
        [col.rule]: 'rule',
        [col.biz]: 'biz',
        [col.reason]: 'reason',
        [col.age]: 'age',
    },
};

const AdminReviewQueueView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.admin.reviewQueue'});
    const {t: tSearch} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.common.search'});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportAdminReviewQueue = operatorAccessLevels.packages.bizPoint.exportAdminReviewQueue;
    const {configureNotebookTags} = useConfigureNotebook('biz-point-admin-review-queue');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [sourceItems, setSourceItems] = useState(mockReviewQueue);
    const [data, setData] = useState([]);
    const [actionDialog, setActionDialog] = useState(null);

    /**
     * Configures notebook tags and behavioral analytics for the review queue.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointAdminReviewQueue);
        configureBehavioralTool((tags) => tags.bizPointAdminReviewQueue);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() => [
        {title: t('wallet'), name: col.wallet, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('rule'), name: col.rule, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('amount'), name: col.biz, type: DataGridColumnTypes.number, alignment: DataGridColumnAlignments.right},
        {title: t('reason'), name: col.reason, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('age'), name: col.age, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {
            title: t('actions'),
            name: col.actions,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            pinned: true,
            pinnedToggleable: false,
            visibilityToggleable: false,
            width: 200,
        },
    ], [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(
        () => (dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo),
        [dataGridPageSize],
    );

    /**
     * Loads review queue items from mock data.
     */
    const search = useCallback(async (args) => {
        await Promise.resolve();
        // TODO: Replace with real API when endpoint is available
        const page = paginateMockItems(sourceItems, args, searchOptions);
        if (!isMounted()) {
            return;
        }
        setData(page);
    }, [isMounted, sourceItems]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: ''},
    }, true);

    /**
     * Applies keyword filter via useSearch (panel SearchInput pattern).
     */
    const searchByKeyword = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.toString()?.trim() ?? '',
        });
    }, [searchData]);

    const getExportInfo = useCallback(async () => {
        const items = await Promise.resolve(paginateMockItems(sourceItems, {
            filters: searchData.filters,
            orderBy: searchData.orderBy,
            paginationInfo: {currentPage: 1, pageSize: 100000},
            setPaginationInfo: () => {},
        }, searchOptions));
        if (!items?.length) {
            return undefined;
        }
        const excludeNames = ['actions'];
        const valueResolvers = {};
        const exportColumns = columns.filter((column) => {
            if (!column?.name || excludeNames.includes(column.name)) {
                return false;
            }
            if (column.type === DataGridColumnTypes.element && !valueResolvers[column.name]) {
                return false;
            }
            return true;
        });
        const body = items.map((item) => {
            const row = {};
            for (const column of exportColumns) {
                const header = typeof column.title === 'function'
                    ? column.title()?.toString()
                    : (column.title ?? column.name);
                if (valueResolvers[column.name]) {
                    row[header] = valueResolvers[column.name](item) ?? '--';
                } else {
                    const value = item[column.name];
                    row[header] = value == null || value === '' ? '--' : String(value);
                }
            }
            return row;
        });
        return {title: t('exportTitle'), body};
    }, [columns, searchData.filters, searchData.orderBy, sourceItems, t]);

    /**
     * Removes a resolved queue item from the local list.
     */
    const handleResolved = useCallback(({itemId}) => {
        setSourceItems((prev) => prev.filter((i) => i.id !== itemId));
        setActionDialog(null);
        searchData.performSearch();
    }, [searchData]);

    /**
     * Closes the resolve dialog.
     */
    const handleCloseDialog = useCallback(() => {
        setActionDialog(null);
    }, []);

    const rows = useMemo(() => data.map((row) => ({
        key: row.id,
        data: row,
        cells: {
            [col.wallet]: row.wallet,
            [col.rule]: row.rule,
            [col.biz]: row.biz,
            [col.reason]: row.reason,
            [col.age]: row.age,
            [col.actions]:
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => setActionDialog({item: row, action: 'approve'})}
                    >
                        {t('approve')}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setActionDialog({item: row, action: 'reject'})}
                    >
                        {t('reject')}
                    </Button>
                </Stack>,
        },
    })), [data, t]);

    return (
        <Box>
            <PageHeader title={t('title')}/>

            {sourceItems.length === 0 && (
                <Alert severity="success">{t('empty')}</Alert>
            )}

            {sourceItems.length > 0 && (
                <>
                    <Box sx={{mb: 3}}>
                        <SearchInput
                            loading={searchData.loading}
                            autoSearch
                            immediateSearch
                            search={searchByKeyword}
                            initialValue={searchData.filters?.keyword ?? undefined}
                            label={tSearch('keyword')}
                        />
                    </Box>

                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{columns}}
                        columns={dataGridColumns}
                        rows={rows}
                        state={{
                            pagination: searchData.paginationInfo,
                            sortBy: searchData.orderBy,
                            loading: {state: searchData.loading},
                            density: dataGridDensity,
                        }}
                        hideFooterActions
                        onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                        onCurrentPageChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                        onPageSizeChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                        getExportInfo={canExportAdminReviewQueue ? getExportInfo : undefined}
                    />
                </>
            )}

            <ResolveQueueItemDialog
                open={Boolean(actionDialog)}
                item={actionDialog?.item}
                action={actionDialog?.action}
                onClose={handleCloseDialog}
                onResolved={handleResolved}
            />
        </Box>
    );
};

export default AdminReviewQueueView;
